import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user dismissed before (with 7-day cooldown)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 md:bottom-4 md:left-auto md:right-4 md:max-w-sm z-40 animate-in slide-in-from-bottom-4" data-testid="pwa-install-banner">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary rounded-lg p-2 shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-[#2C3E2D]">{t('pwa.install_title', 'Installeer FamilyPlate')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('pwa.install_description', 'Voeg toe aan je startscherm voor snelle toegang')}</p>
          </div>
          <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="flex-1 bg-primary hover:bg-primary-hover text-white text-xs" onClick={handleInstall} data-testid="pwa-install-btn">
            <Download className="w-3 h-3 mr-1" />
            {t('pwa.install_btn', 'Installeren')}
          </Button>
          <Button size="sm" variant="ghost" className="text-xs text-gray-500" onClick={handleDismiss}>
            {t('pwa.later', 'Later')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
